import {
    DialogContent,
    DialogAwareContentContainer,
    DialogAwareButtonContainer,
    Button,
    Typography,
    IconButton,
    Icon,
} from "@/components";
import { useEffect, useState } from "react";
import { usePersistentQueryValue, useWalletConnection } from "@/hooks";
import { useIsKnownUser } from "../../hooks/wallet/useIsKnownUser";

export interface WaitlistInterceptorDialogProps {
    forceOpen?: boolean;
    onClose?: () => void;
}

function useAuthenticate(onClose: () => void) {
    const [isSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { isConnected, address } = useWalletConnection();

    const { isKnownUser, referralCode } = useIsKnownUser(address);

    const handleSubmit = async () => {
        onClose?.();
        return;
    };

    useEffect(() => {
        setError(null);
    }, [address]);

    useEffect(() => {
        if (isKnownUser) {
            onClose?.();
        }
    }, [isKnownUser]);

    return { isConnected, error, isSubmitting, handleSubmit, isKnownUser, referralCode };
}

export function WaitlistInterceptorDialog({ onClose }: WaitlistInterceptorDialogProps) {
    const { value: referrerCode, setValue: setReferrerCode } = usePersistentQueryValue("ref");
    const { isConnected, isSubmitting, error, handleSubmit, isKnownUser, referralCode } = useAuthenticate(
        onClose ?? (() => {}),
    );

    let buttonText = "";

    if (isKnownUser) {
        buttonText = "Close";
    } else if (!isConnected) {
        buttonText = "Connect wallet";
    } else if (isSubmitting) {
        buttonText = "Loading...";
    } else {
        buttonText = "Sign message";
    }

    const handleCopyReferralCode = () => {
        if (referralCode) {
            const referralLink = `${window.location.origin}${window.location.pathname}?ref=${encodeURIComponent(
                referralCode,
            )}`;
            navigator.clipboard.writeText(referralLink);
        }
    };

    return (
        <DialogContent>
            <DialogAwareContentContainer isDialog>
                <div className="space-y-4">
                    <Typography className="text-dark">
                        {!isConnected && "Connect your wallet to get started"}
                    </Typography>

                    <div className="flex flex-col space-y-2">
                        <Typography variant="body2" className="text-dark">
                            {!isKnownUser ? "Referrer Code" : "Referral Code"}
                        </Typography>
                        <div className="flex flex-row items-center space-x-2">
                            <input
                                type="text"
                                value={isKnownUser ? (referralCode ?? "") : (referrerCode ?? "")}
                                onChange={(e) => setReferrerCode(e.target.value)}
                                className="border border-light rounded-md p-3 card-bg text-primary placeholder-gray-500 flex-1"
                                placeholder={isKnownUser ? "Enter referral code..." : "Enter referrer code..."}
                                disabled={isKnownUser}
                            />
                            {isKnownUser && (
                                <div className="flex-shrink-0">
                                    <IconButton
                                        onClick={handleCopyReferralCode}
                                        className="!p-2 min-w-[40px] min-h-[40px] w-[40px] h-[40px] flex items-center justify-center"
                                    >
                                        <Icon name="Copy" className="text-dark" />
                                    </IconButton>
                                </div>
                            )}
                        </div>
                        {!isKnownUser && (
                            <Typography variant="caption" className="text-dark">
                                If someone referred you to infiniFi, enter their referral code here to give them credit
                                and earn bonus points.
                            </Typography>
                        )}
                        {isKnownUser && (
                            <Typography variant="caption" className="text-dark">
                                You can invite your friends to infiniFi using this referral code and gain bonus points.
                            </Typography>
                        )}
                    </div>

                    {error && (
                        <Typography variant="body2" className="text-red-500">
                            {error}
                        </Typography>
                    )}
                </div>
            </DialogAwareContentContainer>

            <DialogAwareButtonContainer isDialog>
                <Button
                    onClick={handleSubmit}
                    disabled={isSubmitting || (buttonText === "Sign message" && !referrerCode)}
                    isLoading={isSubmitting}
                    fullWidth
                    variant="primary"
                >
                    {buttonText}
                </Button>
            </DialogAwareButtonContainer>
        </DialogContent>
    );
}
